import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../../requests/useRequests'
import ProductsFilter from './components/ProductsFilter'
import ProductsList from './components/ProductsList'
import Pagination from '../Common/Pagination'
import Loader from '../Common/Loader'
import { useBrandDetails } from '../../hooks/useBrandDetails'
import { PageBanner } from '../Banners/PageBanner'

import { State } from '../../types/state'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'

const BrandDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const requests = useRequests()
  const brandId = parseInt(id || '0')
  const { isSuperadmin } = useSelector((state: State) => state.user)

  const {
    brand,
    products,
    loading,
    searchQuery,
    selectedCategory,
    currentPage,
    totalPages,
    loadProducts,
    handleSearch,
    handleCategoryChange,
    handlePageChange,
    showToast
  } = useBrandDetails(brandId, navigate)

  const handleAddProduct = () => {
    navigate(`/products/add?brandId=${brandId}`)
  }

  const handleEditBrand = () => {
    navigate(`/brands/edit/${brandId}`)
  }

  const handleEditProduct = (productId: number) => {
    navigate(`/products/${productId}/edit`)
  }

  const handleDeleteBrand = async () => {
    if (window.confirm('Czy na pewno chcesz usunąć tę markę? Spowoduje to również usunięcie wszystkich jej produktów.')) {
      try {
        await requests.deleteBrand(brandId)
        showToast('Marka została usunięta')
        navigate('/brands')
      } catch (error) {
        console.error('Błąd podczas usuwania marki:', error)
        showToast('Nie udało się usunąć marki')
      }
    }
  }

  const handleDeleteProduct = async (productId: number) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten produkt?')) {
      try {
        await requests.deleteProduct(productId)
        showToast('Produkt został usunięty')
        loadProducts()
      } catch (error) {
        console.error('Błąd podczas usuwania produktu:', error)
        showToast('Wystąpił błąd podczas usuwania produktu')
      }
    }
  }

  const handleViewAllProducts = () => {
    navigate(`/products?brand=${brandId}`)
  }

  if (loading || (brand == null)) {
    return <Loader />
  }

  return (
    <>
      <Header />
      <PageBanner title={brand.name} description={`Produkty marki ${brand.name}`} />

      <section className='brand-details tw-py-16 md:tw-py-20 lg:tw-py-24'>
        <div className='container'>
          <div className='row'>
            {/* Szczegóły marki - lewa strona */}
            <div className='col-lg-4 mb-4 mb-lg-0'>
              <div className='brand-details-sidebar'>
                <div className='brand-logo mb-4'>
                  <img
                    src={brand.logo || brand.image || '/assets/images/brand-placeholder.jpg'}
                    alt={brand.name}
                    className='img-fluid rounded'
                  />
                </div>

                <div className='brand-info'>
                  <h2 className='brand-info__title mb-3'>{brand.name}</h2>
                  <p className='brand-info__desc mb-4'>{brand.description || 'Brak opisu'}</p>

                  <div className='brand-info__meta mb-4'>
                    <div className='brand-info__meta-item mb-2'>
                      <span className='brand-info__meta-label'>Strona internetowa:</span>
                      <span className='brand-info__meta-value'>
                        {brand.website
                          ? (
                            <a href={brand.website} target='_blank' rel='noopener noreferrer'>
                              {brand.website}
                            </a>
                            )
                          : 'Brak'}
                      </span>
                    </div>
                    <div className='brand-info__meta-item mb-2'>
                      <span className='brand-info__meta-label'>Kontakt:</span>
                      <span className='brand-info__meta-value'>{brand.contact || 'Brak'}</span>
                    </div>
                    <div className='brand-info__meta-item mb-2'>
                      <span className='brand-info__meta-label'>Facebook:</span>
                      <span className='brand-info__meta-value'>
                        {brand.facebook
                          ? (
                            <a href={brand.facebook} target='_blank' rel='noopener noreferrer'>
                              {brand.facebook}
                            </a>
                            )
                          : 'Brak'}
                      </span>
                    </div>
                    <div className='brand-info__meta-item mb-2'>
                      <span className='brand-info__meta-label'>Instagram:</span>
                      <span className='brand-info__meta-value'>
                        {brand.instagram
                          ? (
                            <a href={brand.instagram} target='_blank' rel='noopener noreferrer'>
                              {brand.instagram}
                            </a>
                            )
                          : 'Brak'}
                      </span>
                    </div>
                    <div className='brand-info__meta-item'>
                      <span className='brand-info__meta-label'>YouTube:</span>
                      <span className='brand-info__meta-value'>
                        {brand.youtube
                          ? (
                            <a href={brand.youtube} target='_blank' rel='noopener noreferrer'>
                              {brand.youtube}
                            </a>
                            )
                          : 'Brak'}
                      </span>
                    </div>
                  </div>

                  {isSuperadmin && <div className='brand-actions'>
                    <button
                      className={`
                                      tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                      onClick={handleEditBrand}
                    >
                      <i className='fas fa-edit mr-1' /> Edytuj
                    </button>

                    <button
                      className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                      onClick={handleDeleteBrand}
                    >
                      <i className='fas fa-trash-alt mr-1' /> Usuń
                    </button>
                  </div>}
                </div>
              </div>
            </div>

            {/* Lista produktów - prawa strona */}
            <div className='col-lg-8'>
              <div className='products-section'>
                <ProductsFilter
                  brandName={brand.name}
                  searchQuery={searchQuery}
                  onSearch={handleSearch}
                  onViewAll={handleViewAllProducts}
                  onAddProduct={handleAddProduct}
                />

                <ProductsList
                  products={products}
                  onEdit={handleEditProduct}
                  onDelete={handleDeleteProduct}
                  emptyMessage='Nie znaleziono produktów spełniających wybrane kryteria'
                  onClearFilters={() => {
                    handleSearch({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)
                    handleCategoryChange('')
                  }}
                />

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}

                <div className='mt-4'>
                  <button
                    className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-lg tw-transition-colors
                                      tw-text-teal
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-white hover:tw-bg-opacity-90 focus:tw-bg-white
                                    `}
                    onClick={() => navigate('/brands')}
                  >
                    <i className='fas fa-arrow-left mr-2' />
                    Powrót do listy marek
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default BrandDetails
